export type GenerationsStepId =
  | "starting-point"
  | "setup"
  | "accept-pilot"
  | "propagate"
  | "accept-collection"
  | "publish";

// Steps 1-3 build and accept a single pilot image; steps 4-6 propagate it
// across a collection. Split into two route/state groups so each half can
// share state (e.g. the accepted pilot, the selected collection) without
// threading it through the other half.
export type GenerationsStepGroupId = "pilot" | "collection";

export interface GenerationsStepDef {
  id: GenerationsStepId;
  label: string;
  group: GenerationsStepGroupId;
}

// Every routable step (`new/pilot/$stepId`, `new/collection/$stepId`), in
// flow order. The user-facing progress bar is the shorter GENERATIONS_STEPS.
export const GENERATIONS_ROUTE_STEPS: GenerationsStepDef[] = [
  { id: "starting-point", label: "Choose a starting point", group: "pilot" },
  { id: "setup", label: "Set up and run", group: "pilot" },
  { id: "accept-pilot", label: "Accept the pilot", group: "pilot" },
  { id: "propagate", label: "Propagate to collection", group: "collection" },
  {
    id: "accept-collection",
    label: "Accept the collection",
    group: "collection",
  },
  { id: "publish", label: "Publish", group: "collection" },
];

// The three milestones the progress bar shows, visible only once the user is
// inside the flow (past the starting point and blueprint picker).
export const GENERATIONS_STEPS: GenerationsStepDef[] = (
  ["setup", "accept-pilot", "propagate"] as const
).map((id) => GENERATIONS_ROUTE_STEPS[generationsStepIndex(id)]);

// Route steps after "propagate" stay on that milestone; publish completes all.
const PROGRESS_INDEX_BY_STEP: Record<GenerationsStepId, number> = {
  "starting-point": -1,
  setup: 0,
  "accept-pilot": 1,
  propagate: 2,
  "accept-collection": 2,
  publish: GENERATIONS_STEPS.length,
};

export function generationsProgressStepIndex(
  stepId: GenerationsStepId,
): number {
  return PROGRESS_INDEX_BY_STEP[stepId];
}

export function generationsStepIndex(stepId: GenerationsStepId): number {
  return GENERATIONS_ROUTE_STEPS.findIndex((step) => step.id === stepId);
}

export function generationsStepGroup(
  stepId: GenerationsStepId,
): GenerationsStepGroupId {
  return GENERATIONS_ROUTE_STEPS[generationsStepIndex(stepId)].group;
}

export function generationsStepsInGroup(
  group: GenerationsStepGroupId,
): GenerationsStepDef[] {
  return GENERATIONS_ROUTE_STEPS.filter((step) => step.group === group);
}

export function isGenerationsStepIdInGroup(
  value: string,
  group: GenerationsStepGroupId,
): value is GenerationsStepId {
  return generationsStepsInGroup(group).some((step) => step.id === value);
}

export const DEFAULT_STEP_ID_BY_GROUP: Record<
  GenerationsStepGroupId,
  GenerationsStepId
> = {
  pilot: generationsStepsInGroup("pilot")[0].id,
  collection: generationsStepsInGroup("collection")[0].id,
};

// The two route paths a step can live under — `new/pilot/$stepId` or
// `new/collection/$stepId` — used to navigate across the group boundary
// (e.g. "Continue" from the last pilot step to the first collection step).
export function generationsStepRoutePath(
  stepId: GenerationsStepId,
):
  | "/generations/$siteId/new/pilot/$stepId"
  | "/generations/$siteId/new/collection/$stepId" {
  return generationsStepGroup(stepId) === "pilot"
    ? "/generations/$siteId/new/pilot/$stepId"
    : "/generations/$siteId/new/collection/$stepId";
}
